import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Truck, Plus, MapPin, Activity, Loader2,
  ShieldAlert, Clock, CheckCircle2, ArrowRight
} from "lucide-react";

// ─── IMPORTED LAYOUT COMPONENTS ───────────────────────────────────────────────
import Sidebar from "./Includes/Sidebar";
import TopBar from "./Includes/TopBar";
import BottomNav from "./Includes/BottomNav";
import { NAV_ITEMS, Icon } from "./Includes/navConfig";

// ─── UTILITIES & SMALL COMPONENTS ──────────────────────────────────────────────

function StatusPill({ status, isOpen }) {
  if (isOpen) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
        <Clock className="w-2.5 h-2.5" /> Pending Match
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 whitespace-nowrap">
      <CheckCircle2 className="w-2.5 h-2.5" /> Assigned
    </span>
  );
}

function StatCard({ label, value, sub, icon: IconComponent, iconBg, trend, trendUp, onClick }) {
  return (
    <div 
      onClick={onClick}
      className={`bg-white border rounded-2xl p-4 shadow-sm transition-all duration-200 
        ${onClick 
          ? "border-gray-100 hover:border-blue-200 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] cursor-pointer" 
          : "border-gray-100 hover:shadow-md"
        }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        {trend && (
          <span className={`text-xs font-semibold ${trendUp ? "text-green-600" : "text-red-500"}`}>
            {trend}
          </span>
        )}
      </div>
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function RouteMap({ origin, destination, originCoords, destCoords, meetingCoords, meetingName, distanceMiles }) {
  const fromCoords = originCoords || [39.8283, -98.5795];
  const toCoords   = destCoords || [39.8283, -98.5795];

  const midLat  = (fromCoords[0] + toCoords[0]) / 2;
  const midLng  = (fromCoords[1] + toCoords[1]) / 2;
  const dLat    = toCoords[0] - fromCoords[0];
  const dLng    = toCoords[1] - fromCoords[1];
  const distMi  = distanceMiles ? Math.round(distanceMiles) : (() => { const r = (x) => (x * Math.PI) / 180; const h = Math.sin(r(dLat) / 2) ** 2 + Math.cos(r(fromCoords[0])) * Math.cos(r(toCoords[0])) * Math.sin(r(dLng) / 2) ** 2; return Math.round(2 * 3958.8 * Math.asin(Math.sqrt(h)) * 1.18); })();
  const zoom = distMi < 80 ? 8 : distMi < 200 ? 7 : distMi < 400 ? 6 : 5;

  const mapHtml = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<style>*{margin:0;padding:0;box-sizing:border-box;}html,body,#map{width:100%;height:100%;}</style>
</head><body>
<div id="map"></div>
<script>
var map = L.map('map', {zoomControl:true, attributionControl:false}).setView([${midLat},${midLng}],${zoom});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18}).addTo(map);

var fromPt = [${fromCoords[0]},${fromCoords[1]}];
var toPt   = [${toCoords[0]},${toCoords[1]}];
var dLat = toPt[0] - fromPt[0];
var dLng = toPt[1] - fromPt[1];
var pts = [];
var steps = 20;
for(var i=0;i<=steps;i++){
  var t = i/steps;
  var cx = (fromPt[1]+toPt[1])/2 + dLat*0.15;
  var cy = (fromPt[0]+toPt[0])/2 - dLng*0.15;
  var lat = (1-t)*(1-t)*fromPt[0] + 2*(1-t)*t*cy + t*t*toPt[0];
  var lng = (1-t)*(1-t)*fromPt[1] + 2*(1-t)*t*cx + t*t*toPt[1];
  pts.push([lat,lng]);
}

L.polyline(pts,{color:'#0099a8',weight:4,opacity:0.85,dashArray:null}).addTo(map);
L.polyline(pts,{color:'#005f6b',weight:6,opacity:0.2}).addTo(map);

var originIcon = L.divIcon({html:'<div style="background:#e07b00;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',iconAnchor:[7,7],className:''});
var destIcon   = L.divIcon({html:'<div style="background:#0099a8;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>',iconAnchor:[9,9],className:''});

L.marker(fromPt,{icon:originIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${origin.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});
L.marker(toPt,  {icon:destIcon  }).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${destination.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});

${meetingCoords && meetingCoords[0] ? `
var meetingPt = [${meetingCoords[0]},${meetingCoords[1]}];
var meetIcon = L.divIcon({html:'<div style="background:#7c3aed;width:14px;height:14px;border:3px solid white;border-radius:3px;box-shadow:0 2px 6px rgba(0,0,0,0.4);transform:rotate(45deg);"></div>',iconAnchor:[7,7],className:''});
var meetName = "${meetingName ? meetingName.split(",")[0].replace(/"/g, '\\"').replace(/'/g, "\\'") : 'Meeting Point'}";
L.marker(meetingPt,{icon:meetIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">🚩 ' + meetName + '</b>',{permanent:true,direction:'top', offset:[0,-10]});
var bounds = L.latLngBounds([fromPt, toPt, meetingPt]);
map.fitBounds(bounds, {padding: [30, 30]});
` : ''}
</script>
</body></html>`;

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-inner bg-gray-50 h-48 sm:h-56 relative w-full">
      <iframe srcDoc={mapHtml} style={{ width:"100%", height:"100%", border:"none", display:"block" }} title="Route Map" />
      <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur border border-gray-200/50 rounded-lg p-2 flex items-center justify-between pointer-events-none shadow-sm">
         <span className="text-[10px] font-bold text-gray-700 truncate max-w-[40%]">📍 {origin.split(",")[0]}</span>
         <span className="text-[10px] font-bold text-blue-600">~{distMi} miles</span>
         <span className="text-[10px] font-bold text-gray-700 truncate max-w-[40%] text-right">🏁 {destination.split(",")[0]}</span>
      </div>
    </div>
  );
}

function LoadModal({ load, onClose }) {
  const navigate = useNavigate();

  if (!load) return null;

  const isOpen = load.status?.toLowerCase() === "open";
  
  const commodity = load.type || load.commodity || load.load_type || "—";
  const weight = load.weight || (load.weight_lbs ? `${load.weight_lbs} lbs` : null) || "N/A";
  
  let fee = load.pay || load.posting_fee || load.pay_amount || "0.00";
  if (!String(fee).startsWith('$')) fee = `$${parseFloat(fee).toFixed(2)}`;

  const origin = load.from || load.origin || load.origin_label || "—";
  const destination = load.to || load.destination || load.destination_label || "—";
  
  const hasMap = load.startCoords && load.endCoords;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background:"rgba(0,0,0,0.65)", backdropFilter:"blur(3px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] sm:max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 flex-shrink-0" style={{ background:"#1a2744" }}>
          <div className="flex-1 min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-white truncate">Load Details</h3>
              {load.is_featured && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                  ⚡ Featured
                </span>
              )}
            </div>
            <p className="text-[10px] text-white/40 mt-0.5 truncate">
              #{load.id || load.reference_code} · {isOpen ? "Pending Driver Match" : "Driver Assigned"}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors flex-shrink-0">✕</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-5 space-y-5 sm:space-y-6">
          
          {/* Map & Route */}
          <div>
            {hasMap ? (
              <div className="mb-4">
                 <RouteMap 
                    origin={origin} 
                    destination={destination} 
                    originCoords={load.startCoords} 
                    destCoords={load.endCoords}
                    meetingCoords={load.meetingCoords}
                    meetingName={load.meeting_point}
                 />
              </div>
            ) : null}
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
               <div className="flex-1 min-w-0">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Origin</p>
                 <p className="text-sm font-bold text-gray-900 leading-snug break-words">{origin}</p>
                 {load.origin_zip && <p className="text-xs text-gray-500 mt-0.5">ZIP: {load.origin_zip}</p>}
                 <p className="text-xs font-semibold text-blue-600 mt-2">
                   📅 {load.date || "TBD"} {load.time ? `· 🕐 ${load.time}` : ""}
                 </p>
               </div>
               <ArrowRight className="hidden sm:block w-5 h-5 text-gray-300 flex-shrink-0" />
               <div className="flex-1 min-w-0 sm:text-right border-t sm:border-t-0 border-gray-200 pt-3 sm:pt-0">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Destination</p>
                 <p className="text-sm font-bold text-gray-900 leading-snug break-words">{destination}</p>
                 {load.destination_zip && <p className="text-xs text-gray-500 mt-0.5">ZIP: {load.destination_zip}</p>}
               </div>
            </div>
          </div>

          {/* Meeting Point */}
          {load.meeting_point && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-purple-500 mb-2">🚩 Meeting Point</p>
              <div className="bg-purple-50/50 border border-purple-100 rounded-xl p-4">
                <p className="text-sm font-bold text-gray-900 break-words">{load.meeting_point}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-600">
                  {load.meeting_city && <span>{load.meeting_city}, {load.meeting_state} {load.meeting_zip}</span>}
                  {load.meeting_time && <span className="font-semibold text-purple-700">Time: {load.meeting_time}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Load Specs & Escorts Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Load Specs</p>
              <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 space-y-3">
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Commodity</span>
                  <span className="text-xs font-bold text-gray-900 text-right">{commodity}</span>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Dimensions</span>
                  <span className="text-xs font-bold text-gray-900 text-right">{load.dims || "—"}</span>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Weight</span>
                  <span className="text-xs font-bold text-gray-900 text-right">{weight}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Required Escorts ({load.escorts})</p>
              <div className="flex flex-wrap gap-2">
                 {load.needs_front_escort && <span className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold">Front Escort</span>}
                 {load.needs_chase_escort && <span className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-bold">Chase Escort</span>}
                 {load.needs_height_pole && <span className="px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-xs font-bold">Height Pole</span>}
                 {load.needs_steersman && <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-bold">Steersman</span>}
                 {!load.needs_front_escort && !load.needs_chase_escort && !load.needs_height_pole && !load.needs_steersman && (
                   <span className="text-xs text-gray-400 italic">None specified.</span>
                 )}
              </div>
            </div>
          </div>

          {/* Pay & Rates */}
          <div>
             <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Pay & Allowances</p>
             <div className="bg-green-50 border border-green-100 rounded-xl overflow-hidden">
               <div className="flex justify-between items-center bg-green-100/50 p-3 sm:p-4 border-b border-green-100">
                 <span className="text-sm font-bold text-green-900">Total Est. Pay</span>
                 <span className="text-lg font-black text-green-700">{fee}</span>
               </div>
               <div className="p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                 <div>
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Pay Type</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">{load.pay_type || "—"}</p>
                 </div>
                 <div>
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Mileage Rate</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">{load.mileage_rate ? `$${load.mileage_rate}/mi` : "—"}</p>
                 </div>
                 <div>
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Hourly</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">{load.hourly_rate ? `$${load.hourly_rate}/hr` : "—"}</p>
                 </div>
                 <div>
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Overnight</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">{load.overnight ? "Included ($125)" : "Not Included"}</p>
                 </div>
                 <div>
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Min Daily</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">{load.min_daily_pay ? `$${load.min_daily_pay}` : "—"}</p>
                 </div>
                 <div>
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Pole Premium</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">{load.height_pole_pay ? `$${load.height_pole_pay}` : "—"}</p>
                 </div>
                 <div className="col-span-2">
                   <p className="text-[9px] font-bold text-gray-500 uppercase">Terms & Surcharge</p>
                   <p className="text-xs font-bold text-gray-900 mt-0.5">
                     {load.payment_terms || "TBD"} · {load.fuel_surcharge === "Included" ? "Fuel Inc." : "No Fuel Surcharge"}
                   </p>
                 </div>
               </div>
             </div>
          </div>

          {/* Notes */}
          {load.notes && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Instructions / Notes</p>
              <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-3 sm:p-4">
                <p className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap">{load.notes}</p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button onClick={onClose} className="border border-gray-200 text-gray-700 text-sm font-bold px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl hover:bg-gray-100 transition-colors">
            Close
          </button>
          <button 
            onClick={() => {
              onClose();
              if (isOpen) {
                navigate(`/loads/${load.pk || load.id}/applicants`);
              }
            }}
            className="text-white text-sm font-bold px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl transition-colors hover:opacity-90 shadow-sm whitespace-nowrap" 
            style={{ background:"#1a2744" }}
          >
            {isOpen ? "View Applicants" : "Driver Details"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function LoadCard({ load, onViewDetails }) {
  const isOpen = load.status?.toLowerCase() === "open";
  
  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });
  };
  
  const origin = load.from || load.origin || load.origin_label || "—";
  const destination = load.to || load.destination || load.destination_label || "—";
  const date = load.date || load.departure_date || load.pickup_date;
  const commodity = load.type || load.commodity || load.load_type || "—";
  
  let fee = load.pay || load.posting_fee || load.pay_amount || "0.00";
  if (!String(fee).startsWith('$')) fee = `$${parseFloat(fee).toFixed(2)}`;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
      onClick={() => onViewDetails(load)}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            <StatusPill status={load.status} isOpen={isOpen} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1.5">
            <span className="text-sm font-bold text-gray-900 truncate">{origin}</span>
            <ArrowRight className="hidden sm:block w-3.5 h-3.5 text-gray-300 flex-shrink-0" strokeWidth={2.5} />
            <span className="sm:hidden text-xs text-gray-400">to</span>
            <span className="text-sm font-bold text-gray-900 truncate">{destination}</span>
          </div>
          <p className="text-xs text-gray-400 flex flex-wrap items-center gap-1.5 mt-2 sm:mt-0">
            <Truck className="w-3 h-3 flex-shrink-0" />
            <span className="truncate max-w-[120px] sm:max-w-none">{commodity}</span>
            <span className="text-gray-200">•</span>
            {fmtDate(date)}
          </p>
        </div>
        <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-gray-100 sm:pl-5 flex-shrink-0">
          <div className="sm:text-right">
            <p className="text-[9px] text-gray-400 font-medium uppercase tracking-wider">Marketplace Fee</p>
            <p className="text-base font-bold text-gray-900 mt-0.5">{fee}</p>
          </div>
          <button
            onClick={e => { e.stopPropagation(); onViewDetails(load); }}
            className="text-xs font-bold px-3 py-1.5 rounded-xl transition-all whitespace-nowrap"
            style={isOpen
              ? { border: "1px solid #e5e7eb", background: "#fff", color: "#374151" }
              : { background: "#1a2744", color: "#fff" }
            }
          >
            {isOpen ? "View Status" : "Driver Info"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center border-2 border-dashed border-gray-200 rounded-2xl">
      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
        <MapPin className="w-7 h-7 text-gray-300" />
      </div>
      <h3 className="text-sm font-bold text-gray-800 mb-1">No Active Loads</h3>
      <p className="text-xs text-gray-400 max-w-xs leading-relaxed mb-5">
        You haven't posted any oversize loads yet. Create your first listing to instantly dispatch to our certified driver network.
      </p>
      <Link to="/post-load">
        <button className="text-white text-xs font-bold px-4 py-2.5 rounded-xl flex items-center gap-2 transition-opacity hover:opacity-90" style={{ background: "#1a2744" }}>
          <Plus className="w-4 h-4" /> Post Your First Load
        </button>
      </Link>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function CompanyDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const activeNav = NAV_ITEMS.find(item => item.path === location.pathname)?.key || "dashboard";

  const getUser = () => {
    try { return JSON.parse(localStorage.getItem("user")); } 
    catch { return null; }
  };
  
  const [user, setUser] = useState(getUser());

  // ALL HOOKS MUST GO FIRST (State and Effects)
  const [loads,        setLoads]        = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [loadFilter,   setLoadFilter]   = useState("all");
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [notifCount,   setNotifCount]   = useState(2);

  // 1. Auth check useEffect
  useEffect(() => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    if (!token || !user || user.role !== "company") {
      if (user && user.role !== "company") {
        localStorage.removeItem("user_token");
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
      }
      navigate("/company-login", { replace: true });
    }
  }, [navigate, user]);

  // 2. Fetch loads useEffect
  useEffect(() => {
    const fetchLoads = async () => {
      if (!user?.email) { setIsLoading(false); return; }
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token") || "";

        const res = await fetch(`${apiUrl}/api/loads/company/`, {
          headers: {
            "Content-Type":  "application/json",
            ...(token ? { "Authorization": `Bearer ${token}` } : {}),
          },
        });

        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          const fallbackRes = await fetch(`${apiUrl}/api/loads/`, {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { "Authorization": `Bearer ${token}` } : {}),
            }
          });
          if (fallbackRes.ok) {
             const data = await fallbackRes.json();
             setLoads(data.results || data || []);
          }
          setIsLoading(false);
          return;
        }

        const data = await res.json();
        if (res.ok) {
          setLoads(data.results || data || []);
        }
      } catch (err) {
        console.error("Network error fetching loads:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLoads();
  }, [user?.email]);


  // --------------- EARLY RETURN GOES HERE -----------------
  // Put this AFTER all hooks to prevent the flash, but without breaking React!
  const currentToken = localStorage.getItem("user_token") || localStorage.getItem("access_token");
  if (!currentToken || !user || user.role !== "company") {
    // Renders a totally blank screen while the useEffect above handles the redirect
    return null; 
  }
  // --------------------------------------------------------


  const companyName = user?.company_name || user?.name || "Company";
  const initials    = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  const activePostings = loads.filter(l => l.status?.toLowerCase() === "open").length;
  const assignedLoads  = loads.filter(l => l.status?.toLowerCase() !== "open").length;
  
  const totalSpent = loads.reduce((acc, l) => {
    const payStr = String(l.pay || l.posting_fee || l.pay_amount || "0");
    const numericVal = parseFloat(payStr.replace(/[^0-9.]/g, '')) || 0;
    return acc + numericVal;
  }, 0);

  const filteredLoads = loadFilter === "all"      ? loads
    : loadFilter === "open"     ? loads.filter(l => l.status?.toLowerCase() === "open")
    : loads.filter(l => l.status?.toLowerCase() !== "open");

  const PAGE_TITLES = {
    dashboard: "Dashboard", loads: "My Loads", drivers: "Drivers",
    analytics: "Analytics", documents: "Documents", messages: "Messages", settings: "Settings",
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/company-login");
  };

  if (activeNav !== "dashboard") {
    return (
      <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
        <Sidebar activeNav={activeNav} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} 
          initials={initials} companyName={companyName} loads={loads} activePostings={activePostings} 
          assignedLoads={assignedLoads} totalSpent={totalSpent} handleLogout={handleLogout} />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar title={PAGE_TITLES[activeNav]} setSidebarOpen={setSidebarOpen} initials={initials}
  notifCount={notifCount} setNotifCount={setNotifCount} handleLogout={handleLogout} />
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="text-center w-full max-w-sm">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Icon d={NAV_ITEMS.find(n => n.key === activeNav)?.icon || ""} size="w-7 h-7" />
              </div>
              <p className="text-sm font-bold text-gray-800 mb-1">{PAGE_TITLES[activeNav]}</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                This section is handled by React Router.
              </p>
              <button onClick={() => navigate("/company-dashboard")} className="mt-4 border border-gray-200 text-gray-600 text-xs font-semibold px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors w-full sm:w-auto">
                ← Back to Dashboard
              </button>
            </div>
          </main>
          <BottomNav activeNav={activeNav} setSidebarOpen={setSidebarOpen} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar activeNav={activeNav} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName} loads={loads} activePostings={activePostings}
        assignedLoads={assignedLoads} totalSpent={totalSpent} handleLogout={handleLogout} />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
       <TopBar title="Dashboard" setSidebarOpen={setSidebarOpen} initials={initials} notifCount={notifCount} setNotifCount={setNotifCount} handleLogout={handleLogout} />
        
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5">
            
            {/* Banner */}
            <div className="rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ background: "#1a2744" }}>
              <div>
                <h2 className="text-white font-bold text-base">
                  Welcome back, {companyName}! 👋
                </h2>
                <p className="text-blue-200 text-sm mt-1">
                  {activePostings > 0
                    ? `${activePostings} load${activePostings !== 1 ? "s" : ""} awaiting driver match.`
                    : "Post a load to get started with dispatching."
                  }
                </p>
              </div>
              <Link to="/post-load" className="flex-shrink-0 w-full sm:w-auto">
                <button className="w-full sm:w-auto flex items-center justify-center gap-1.5 bg-white/15 border border-white/25 text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-white/25 transition-colors whitespace-nowrap">
                  <Plus className="w-3.5 h-3.5" /> Post New Load
                </button>
              </Link>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard 
                label="Active Postings" 
                value={activePostings} 
                sub="Awaiting match" 
                icon={Activity} 
                iconBg="bg-blue-50 text-blue-600" 
                trend="↑ 12%" 
                trendUp={true} 
                onClick={() => navigate("/my-loads")}
              />
              <StatCard 
                label="Assigned Drivers" 
                value={assignedLoads} 
                sub="All time" 
                icon={Truck} 
                iconBg="bg-green-50 text-green-600" 
                trend="↑ 5%" 
                trendUp={true} 
                onClick={() => navigate("/drivers")}
              />
              
              <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-sm relative overflow-hidden hidden lg:block">
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-blue-400/10 blur-xl pointer-events-none" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "#1a2744" }}>
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Dispatch a Load</h3>
                  <p className="text-xs text-gray-400 leading-relaxed mb-4">
                    Instantly broadcast your oversize route to our nationwide network of certified pilot car professionals.
                  </p>
                  <Link to="/post-load">
                    <button className="w-full text-white text-xs font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors hover:opacity-90" style={{ background: "#1a2744" }}>
                      <Plus className="w-4 h-4" /> Post New Load
                    </button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5 pb-20 sm:pb-0">
              <div className="xl:col-span-2 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <h2 className="text-sm font-bold text-gray-900">Your Recent Postings</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {["all", "open", "assigned"].map(f => (
                      <button key={f} onClick={() => setLoadFilter(f)}
                        className="px-3 py-1.5 sm:py-1 rounded-full text-[10px] font-semibold capitalize transition-colors flex-1 sm:flex-none"
                        style={loadFilter === f ? { background: "#1a2744", color: "#fff" } : { background: "#e5e7eb", color: "#4b5563" }}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3 bg-white rounded-2xl border border-gray-100">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <p className="text-xs text-gray-400 font-medium">Syncing your dashboard…</p>
                  </div>
                ) : filteredLoads.length === 0 ? (
                  <EmptyState />
                ) : (
                  <div className="space-y-3">
                    {filteredLoads.map(load => (
                      <LoadCard key={load.id} load={load} onViewDetails={l => setSelectedLoad(l)} />
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-4 hidden xl:block">
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                      <ShieldAlert className="w-4.5 h-4.5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Need Support?</p>
                      <p className="text-[10px] text-gray-400 mt-1 leading-relaxed mb-2">
                        Questions about compliance or dispatching? Our team is available 24/7.
                      </p>
                      <span className="text-[10px] font-bold text-blue-600 cursor-pointer hover:underline">
                        Contact Dispatch Team →
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <BottomNav activeNav={activeNav} setSidebarOpen={setSidebarOpen} />
      </div>

      {selectedLoad && <LoadModal load={selectedLoad} onClose={() => setSelectedLoad(null)} />}
    </div>
  );
}